import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { API } from "@/lib/api";

const KEY = "onex_chat";

const PATH_RE = /(\/(?:shipping-policy|refund-policy|policy|track|privacy|terms|support|account|products|p\/[\w-]+)|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/gi;

const Linkify = ({ text }) => text.split(PATH_RE).map((part, i) => i % 2 === 0 ? part
  : part.includes("@") ? <a key={i} href={`mailto:${part}`} className="underline" dir="ltr">{part}</a>
  : <Link key={i} to={part} className="underline font-medium" dir="ltr">{part}</Link>);

export function ChatWidget() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(() => JSON.parse(sessionStorage.getItem(KEY) || "{}").msgs || []);
  const [sid, setSid] = useState(() => JSON.parse(sessionStorage.getItem(KEY) || "{}").sid || null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify({ sid, msgs })); end.current?.scrollIntoView({ block: "end" }); }, [sid, msgs, open]);

  const send = async (e) => {
    e.preventDefault();
    const m = text.trim();
    if (!m || busy) return;
    setText(""); setBusy(true);
    setMsgs((x) => [...x, { role: "user", text: m }, { role: "assistant", text: "" }]);
    const put = (fn) => setMsgs((x) => { const y = [...x]; y[y.length - 1] = fn(y[y.length - 1]); return y; });
    try {
      const r = await fetch(`${API}/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: m, session_id: sid, lang }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "assistant_unavailable");
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const ev = JSON.parse(p.replace(/^data: /, ""));
          if (ev.t) put((last) => ({ ...last, text: last.text + ev.t }));
          if (ev.done) setSid(ev.session_id);
          if (ev.error) throw new Error(ev.error);
        }
      }
    } catch (e2) {
      const code = typeof e2.message === "string" && /^[a-z_]+$/.test(e2.message) ? e2.message : "assistant_unavailable";
      put(() => ({ role: "error", text: t(`errors.${code}`) }));
    } finally { setBusy(false); }
  };

  if (pathname.startsWith("/checkout")) return null;
  const lifted = pathname.startsWith("/p/");
  return (
    <div className={`print:hidden fixed end-5 z-50 ${lifted ? "bottom-24 lg:bottom-5" : "bottom-5"}`}>
      {open && (
        <div data-testid="chat-panel" className="mb-3 w-[min(92vw,380px)] h-[min(70vh,520px)] bg-white rounded-2xl shadow-2xl border border-[#E5E7EB] flex flex-col overflow-hidden">
          <div className="bg-[#0B0D0F] text-white px-4 py-3 flex items-center justify-between">
            <div><p className="font-semibold text-sm">{t("chat.title")}</p><p className="text-[11px] text-white/60">{t("chat.subtitle")}</p></div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="chat-messages">
            <p className="bg-[#F5F7FA] rounded-2xl px-3 py-2 max-w-[85%]">{t("chat.welcome")}</p>
            {msgs.filter((m) => m.text).map((m, i) => (
              <p key={i} data-testid={`chat-msg-${m.role}`} className={`rounded-2xl px-3 py-2 max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "ms-auto bg-[#2563EB] text-white" : m.role === "error" ? "bg-red-50 text-[#B91C1C]" : "bg-[#F5F7FA]"}`} dir="auto">{m.role === "assistant" ? <Linkify text={m.text} /> : m.text}</p>
            ))}
            {busy && <Loader2 data-testid="chat-loading" className="w-4 h-4 animate-spin text-[#6B7280]" />}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="border-t border-[#E5E7EB] p-2 flex gap-2">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder={t("chat.placeholder")} className="input flex-1" />
            <button data-testid="chat-send-btn" disabled={busy || !text.trim()} className="btn-primary px-3 disabled:opacity-50"><Send className="w-4 h-4 rtl:-scale-x-100" /></button>
          </form>
          <p className="text-[10px] text-center muted pb-2">{t("chat.disclaimer")}</p>
        </div>
      )}
      <button data-testid="chat-toggle-btn" onClick={() => setOpen((o) => !o)} aria-label={t("chat.title")}
        className="ms-auto w-14 h-14 rounded-full bg-[#2563EB] text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </div>
  );
}
